import { useAppStore, useMedicationPadItemSelector } from '@/state-management';
import { Alert } from '@montugroup/design-system';
import type { AlertProps } from '@mui/material';
import { useRef } from 'react';
import type {
  MedicationPadAlertError,
  MedicationPadItemCancelData,
  MedicationPadItemType,
  ReissueStatusType,
} from '../../PrescriptionPad';
import { isMedicationOutOfService } from '../../PrescriptionPad';
import { PRODUCT_ALERT_MESSAGE_TEMPLATE_REISSUE } from '../../ProductDetailsCard/ProductDetailsCard.constants';
import { PrescriptionItemAlertType } from '../PrescriptionItemEditor.types';
import { useAlertType, useCancelAlert } from '../hooks';
import type { AvailabilityAlertType } from '../utils/getAvailabilityAlertType.utils';
import { getAvailabilityAlertType } from '../utils/getAvailabilityAlertType.utils';

type AlertMessage = {
  severity: AlertProps['severity'];
  ariaLabel: string;
  description: React.ReactNode;
};

// Worded to read as "<product> {reason} and has been replaced with <product>", so the reasons stay present tense.
const OUT_OF_SERVICE_REASON: Record<AvailabilityAlertType, string> = {
  [PrescriptionItemAlertType.INACTIVE]: 'is unavailable for ordering',
  [PrescriptionItemAlertType.UNAVAILABLE]: 'is unavailable for ordering',
  [PrescriptionItemAlertType.DISCONTINUED]: 'is discontinued',
  [PrescriptionItemAlertType.OUT_OF_STOCK]: 'is out of stock',
};

const describeOutOfServiceReason = (status: Partial<ReissueStatusType>): string =>
  OUT_OF_SERVICE_REASON[getAvailabilityAlertType(status) ?? PrescriptionItemAlertType.OUT_OF_STOCK];

export type PrescriptionItemAlertProps = {
  medicationItemId: string;
};

export const PrescriptionItemAlert = ({ medicationItemId }: PrescriptionItemAlertProps) => {
  const currentMedicationPadItem = useMedicationPadItemSelector(medicationItemId);
  const usePrescriptionAssistant = useAppStore.use.prescriptionAssistant;

  const {
    prescriptionHistory: { itemsToReissue, itemsToCancel },
  } = usePrescriptionAssistant();

  const reissuingMedication = itemsToReissue.find((item) => item.medicationPadItemId === medicationItemId);

  const { cancelAlertData } = useCancelAlert({
    initialMedication: reissuingMedication,
    currentMedicationPadItem: currentMedicationPadItem?.medicationPadItem,
    itemsToCancel,
  });

  const activeAlertType = useAlertType({
    reissueStatus: currentMedicationPadItem?.reissueStatus,
    alertError: currentMedicationPadItem?.alertError ?? null,
    hasActiveCancelAlert: !!cancelAlertData,
  });

  const initialMedicationRef = useRef(reissuingMedication);

  const isOutOfService =
    initialMedicationRef?.current?.reissueStatus &&
    isMedicationOutOfService(initialMedicationRef.current.reissueStatus);

  const getAlertMessage = (
    alertType: PrescriptionItemAlertType | null,
    alertError: MedicationPadAlertError,
    cancelAlertData: MedicationPadItemCancelData | null,
    currentMedicationPadItem: MedicationPadItemType | null,
  ): AlertMessage | null => {
    if (!alertType) {
      return null;
    }

    const inactiveAlertDescription = (
      <>
        This product is currently <strong>unavailable for ordering</strong>. Please select an alternative to proceed.
      </>
    );

    const alertConfig: Record<PrescriptionItemAlertType, AlertMessage> = {
      [PrescriptionItemAlertType.ERROR]: {
        severity: 'error',
        ariaLabel: 'Error message',
        description: alertError && (
          <>
            <strong>{alertError.title} </strong>
            {alertError.description}
          </>
        ),
      },
      [PrescriptionItemAlertType.CANCEL]: {
        severity: 'warning',
        ariaLabel: `${isOutOfService ? 'Out of Service' : 'Reissued'} Medication cancelled`,
        description:
          cancelAlertData &&
          (isOutOfService ? (
            <>
              <strong>{cancelAlertData.medicationName}</strong>{' '}
              {describeOutOfServiceReason(initialMedicationRef.current?.reissueStatus ?? {})} and has been replaced with{' '}
              <strong>{currentMedicationPadItem?.medicationPadItem?.productName ?? 'the selected product'}</strong>.
            </>
          ) : (
            <>
              <strong>
                {[cancelAlertData.supplierName, cancelAlertData.medicationName].filter(Boolean).join(' | ')}
              </strong>{' '}
              will be cancelled and updated to the medication selected.
            </>
          )),
      },
      [PrescriptionItemAlertType.DISCONTINUED]: {
        severity: 'error',
        ariaLabel: 'Discontinued medication',
        description: (
          <>
            This product has been <strong>discontinued</strong> and will be cancelled. Please select an alternative to
            proceed.
          </>
        ),
      },
      [PrescriptionItemAlertType.OUT_OF_STOCK]: {
        severity: 'error',
        ariaLabel: 'Out of stock medication',
        description: (
          <>
            This product is currently <strong>out of stock</strong>. Please select an alternative to proceed.
          </>
        ),
      },
      [PrescriptionItemAlertType.INACTIVE]: {
        severity: 'error',
        ariaLabel: 'Inactive medication',
        description: inactiveAlertDescription,
      },
      [PrescriptionItemAlertType.UNAVAILABLE]: {
        severity: 'error',
        ariaLabel: 'Unavailable medication',
        description: inactiveAlertDescription,
      },
      [PrescriptionItemAlertType.REISSUE]: {
        severity: 'info',
        ariaLabel: 'Re-issued medication',
        description: (
          <>
            <strong>
              {currentMedicationPadItem?.medicationPadItem?.supplierName &&
              currentMedicationPadItem?.medicationPadItem?.productName
                ? `${currentMedicationPadItem?.medicationPadItem?.supplierName} | ${currentMedicationPadItem?.medicationPadItem?.productName}`
                : currentMedicationPadItem?.medicationPadItem?.productName || 'The product'}
            </strong>{' '}
            {PRODUCT_ALERT_MESSAGE_TEMPLATE_REISSUE}.
          </>
        ),
      },
    };

    return alertConfig[alertType];
  };

  const alertMessage = getAlertMessage(
    activeAlertType,
    currentMedicationPadItem?.alertError ?? null,
    cancelAlertData,
    currentMedicationPadItem ?? null,
  );

  if (!alertMessage) {
    return null;
  }

  return (
    <Alert aria-label={alertMessage.ariaLabel} severity={alertMessage.severity}>
      {alertMessage.description}
    </Alert>
  );
};
